/**
 * @file apps/web/src/pages/portal/PortalDashboardPage.tsx
 * @description Client portal dashboard — overview of the client's project progress.
 * Shows test script completion, UAT session status, and pending approvals.
 * Route: /portal/dashboard
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList, Bug, CheckCircle, Clock, FileText, PenLine,
  TrendingUp, AlertCircle,
} from 'lucide-react';
import { usePortalAuth } from '../../lib/portalAuth.js';
import { testScriptsApi, uatApi, approvalsApi, documentsApi } from '../../lib/api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface QuickStat {
  label:   string;
  value:   number | string;
  sub?:    string;
  icon:    React.ReactNode;
  to:      string;
  intent?: 'neutral' | 'success' | 'warning' | 'danger';
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

function StatCard({ stat }: { stat: QuickStat }) {
  const intentClass = {
    neutral: 'bg-sky text-blue',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    danger:  'bg-danger/10 text-danger',
  }[stat.intent ?? 'neutral'];

  return (
    <Link
      to={stat.to}
      className="card p-5 flex items-start gap-3 hover:ring-1 hover:ring-blue/20 transition-all group"
    >
      <div className={['w-9 h-9 rounded-lg flex items-center justify-center shrink-0', intentClass].join(' ')}>
        {stat.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-2xl font-display font-bold text-ink leading-none">{stat.value}</p>
        <p className="text-sm font-medium text-muted mt-0.5 group-hover:text-ink transition-colors">
          {stat.label}
        </p>
        {stat.sub && (
          <p className="text-[11px] font-mono text-muted/60 mt-0.5">{stat.sub}</p>
        )}
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

function ProgressBar({ value, label }: { value: number; label: string }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted font-medium">{label}</span>
        <span className="font-mono text-blue">{value}%</span>
      </div>
      <div className="h-2 rounded-full bg-[rgba(8,17,31,0.06)] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${value}%`, backgroundColor: 'var(--blue)' }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PortalDashboardPage
// ---------------------------------------------------------------------------

export function PortalDashboardPage() {
  const { user, devHeaders } = usePortalAuth();

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Stats
  const [totalScripts, setTotalScripts] = useState(0);
  const [passedScripts, setPassedScripts] = useState(0);
  const [openSessions, setOpenSessions]   = useState(0);
  const [openDefects, setOpenDefects]     = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [docCount, setDocCount]           = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function load() {
      try {
        const projectId = user!.projectId;
        const [scripts, sessions, defects, approvals, docs] = await Promise.all([
          testScriptsApi.list(projectId, devHeaders) as Promise<{ status: string }[]>,
          uatApi.listSessions(projectId, devHeaders) as Promise<{ status: string }[]>,
          uatApi.listDefects(projectId, devHeaders) as Promise<{ status: string }[]>,
          approvalsApi.list(projectId, devHeaders) as Promise<{ status: string }[]>,
          documentsApi.list(projectId, devHeaders) as Promise<unknown[]>,
        ]);
        if (cancelled) return;

        setTotalScripts(scripts.length);
        setPassedScripts(scripts.filter((s) => s.status === 'passed').length);
        setOpenSessions(sessions.filter((s) => s.status !== 'completed').length);
        setOpenDefects(defects.filter((d) => d.status !== 'resolved' && d.status !== 'closed').length);
        setPendingApprovals(approvals.filter((a) => a.status === 'pending').length);
        setDocCount(docs.length);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user?.projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const passRate = totalScripts > 0 ? Math.round((passedScripts / totalScripts) * 100) : 0;

  const stats: QuickStat[] = [
    {
      label:  'Test Scripts',
      value:  totalScripts,
      sub:    `${passedScripts} passed`,
      icon:   <ClipboardList size={18} />,
      to:     '/portal/scripts',
      intent: passRate >= 80 ? 'success' : passRate >= 50 ? 'warning' : 'neutral',
    },
    {
      label:  'Open Defects',
      value:  openDefects,
      icon:   <Bug size={18} />,
      to:     '/portal/scripts',
      intent: openDefects > 5 ? 'danger' : openDefects > 0 ? 'warning' : 'success',
    },
    {
      label:  'Pending Approvals',
      value:  pendingApprovals,
      icon:   <Clock size={18} />,
      to:     '/portal/approvals',
      intent: pendingApprovals > 0 ? 'warning' : 'success',
    },
    {
      label:  'Documents',
      value:  docCount,
      icon:   <FileText size={18} />,
      to:     '/portal/fdd',
      intent: 'neutral',
    },
  ];

  return (
    <div className="p-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-ink">
          Welcome{user && !user.fullName.includes('@') ? `, ${user.fullName.split(' ')[0]}` : ''}
        </h1>
        <p className="text-sm text-muted mt-1">
          Here's the current status of your ERP implementation project.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-4 animate-pulse">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1,2,3,4].map((i) => <div key={i} className="card h-24 bg-gray/60" />)}
          </div>
          <div className="card h-40 bg-gray/60" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stats.map((s) => <StatCard key={s.label} stat={s} />)}
          </div>

          {/* Progress summary */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={15} className="text-blue" />
              <h2 className="font-display text-sm font-semibold text-ink">Project Progress</h2>
            </div>
            <div className="space-y-3">
              <ProgressBar value={passRate} label="Test Script Pass Rate" />
              <ProgressBar
                value={totalScripts > 0 ? Math.round(((totalScripts - openDefects) / totalScripts) * 100) : 100}
                label="Defect Resolution"
              />
            </div>
          </div>

          {/* Quick links */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { to: '/portal/scripts',   label: 'Review Scripts',  icon: <ClipboardList size={16} /> },
              { to: '/portal/fdd',       label: 'View Documents',  icon: <FileText size={16} /> },
              { to: '/portal/approvals', label: 'Approvals',       icon: <CheckCircle size={16} /> },
              { to: '/portal/signoff',   label: 'Sign Off',        icon: <PenLine size={16} /> },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="card p-3 flex flex-col items-center gap-2 text-center
                           hover:ring-1 hover:ring-blue/20 transition-all group"
              >
                <span className="text-muted group-hover:text-blue transition-colors">
                  {item.icon}
                </span>
                <p className="text-xs font-medium text-muted group-hover:text-ink transition-colors">
                  {item.label}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
